import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Lightbulb,
  ChevronRight,
  ChevronDown,
  MapPin,
  BookOpen,
  TrendingUp,
  Target,
  ExternalLink
} from 'lucide-react';
import type { SmartSuggestion } from '../types/relationships';
import { suggestionEngine } from '../lib/suggestionEngine';

interface SmartSuggestionsProps {
  frameworkId?: string;
  contextual?: boolean;
  maxSuggestions?: number;
}

export const SmartSuggestions: React.FC<SmartSuggestionsProps> = ({
  frameworkId,
  contextual = false,
  maxSuggestions = 10,
}) => {
  const [suggestions, setSuggestions] = useState<SmartSuggestion[]>([]);
  const [isExpanded, setIsExpanded] = useState(true);
  const [groupBy, setGroupBy] = useState<'type' | 'priority'>('type');

  useEffect(() => {
    const fetchSuggestions = () => {
      if (frameworkId) {
        const frameworkSuggestions = suggestionEngine.generateFrameworkSuggestions(frameworkId);
        setSuggestions(frameworkSuggestions.slice(0, maxSuggestions));
      } else if (contextual) {
        const contextualSuggestions = suggestionEngine.generateContextualSuggestions();
        setSuggestions(contextualSuggestions.slice(0, maxSuggestions));
      }
    };

    fetchSuggestions();
  }, [frameworkId, contextual, maxSuggestions]);

  if (suggestions.length === 0) return null;

  const groupedSuggestions = groupBy === 'type' 
    ? groupByType(suggestions)
    : [{ type: 'all', suggestions }];

  return (
    <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-primary-200 overflow-hidden">
      {/* Header */}
      <div
        className="flex items-center justify-between p-4 bg-gradient-to-r from-primary-50 to-sage-50 cursor-pointer hover:from-primary-100 hover:to-sage-100 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <Lightbulb className="h-6 w-6 text-primary-600" />
          <div>
            <h3 className="font-bold text-charcoal-900">Smart Suggestions</h3>
            <p className="text-xs text-charcoal-600">
              {suggestions.length} relevant {suggestions.length === 1 ? 'connection' : 'connections'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Group By Toggle */}
          {isExpanded && (
            <select
              value={groupBy}
              onChange={(e) => {
                e.stopPropagation();
                setGroupBy(e.target.value as 'type' | 'priority');
              }}
              onClick={(e) => e.stopPropagation()}
              className="text-xs px-2 py-1 border border-primary-300 rounded bg-[var(--bg-raised)] text-charcoal-700"
            >
              <option value="type">By Type</option>
              <option value="priority">By Priority</option>
            </select>
          )}
          {isExpanded ? (
            <ChevronDown className="h-5 w-5 text-charcoal-600" />
          ) : (
            <ChevronRight className="h-5 w-5 text-charcoal-600" />
          )}
        </div>
      </div>

      {/* Content */}
      {isExpanded && (
        <div className="p-4 space-y-4 max-h-96 overflow-y-auto">
          {groupedSuggestions.map((group, groupIdx) => (
            <div key={groupIdx}>
              {group.type !== 'all' && (
                <h4 className="text-xs font-semibold text-charcoal-600 uppercase tracking-wide mb-2 flex items-center gap-2">
                  {getTypeIcon(group.type)}
                  {getTypeLabel(group.type)}
                </h4>
              )}
              <div className="space-y-2">
                {group.suggestions.map((suggestion, idx) => (
                  <SuggestionCard key={idx} suggestion={suggestion} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Suggestion Card Component
interface SuggestionCardProps {
  suggestion: SmartSuggestion;
}

const SuggestionCard: React.FC<SuggestionCardProps> = ({ suggestion }) => {
  const Component = suggestion.targetPath ? Link : 'div';
  const props = suggestion.targetPath ? { to: suggestion.targetPath } : {};

  return (
    <Component
      {...props}
      className={`
        block p-3  border-2 transition-all
        ${suggestion.targetPath 
          ? 'hover:border-primary-400 hover:shadow-md cursor-pointer bg-[var(--bg-raised)]'
          : 'bg-charcoal-50 border-charcoal-200'
        }
        ${getPriorityBorder(suggestion.priority)}
      `}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <span className="text-2xl flex-shrink-0">{suggestion.icon}</span>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h5 className="font-semibold text-charcoal-900 text-sm mb-1">
            {suggestion.title}
          </h5>
          <p className="text-xs text-charcoal-600 leading-relaxed mb-2">
            {suggestion.description}
          </p>
          {suggestion.context && (
            <p className="text-xs text-charcoal-500 italic">
              {suggestion.context}
            </p>
          )}
        </div>

        {/* Navigation Arrow */}
        {suggestion.targetPath && (
          <ExternalLink className="h-4 w-4 text-primary-600 flex-shrink-0 mt-1" />
        )}
      </div>
    </Component>
  );
};

// Helper Functions

function groupByType(suggestions: SmartSuggestion[]): Array<{ type: string; suggestions: SmartSuggestion[] }> {
  const groups: Record<string, SmartSuggestion[]> = {};
  
  suggestions.forEach(suggestion => {
    if (!groups[suggestion.type]) {
      groups[suggestion.type] = [];
    }
    groups[suggestion.type].push(suggestion);
  });

  const typeOrder = ['prerequisite', 'spatial', 'next-step', 'related', 'glossary'];
  
  return typeOrder
    .filter(type => groups[type])
    .map(type => ({ type, suggestions: groups[type] }));
}

function getTypeIcon(type: string): React.ReactNode {
  const icons: Record<string, React.ReactNode> = {
    'spatial': <MapPin className="h-3 w-3 text-sage-600" />,
    'prerequisite': <Target className="h-3 w-3 text-primary-600" />,
    'next-step': <TrendingUp className="h-3 w-3 text-[var(--mustard)]" />,
    'related': <BookOpen className="h-3 w-3 text-coral-600" />,
    'glossary': <BookOpen className="h-3 w-3 text-charcoal-600" />,
  };
  return icons[type] || null;
}

function getTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    'spatial': 'Where to Find',
    'prerequisite': 'Learn First',
    'next-step': 'Build On This',
    'related': 'Related Frameworks',
    'glossary': 'Key Terms',
  };
  return labels[type] || type;
}

function getPriorityBorder(priority: number): string {
  if (priority >= 9) return 'border-[var(--mustard)]';
  if (priority >= 7) return 'border-primary-300';
  if (priority >= 5) return 'border-sage-300';
  return 'border-charcoal-200';
}

